import React from "react";
import { useNavigate } from "react-router-dom";
import { useRecoSummary } from "../hooks/useRecoSummary";
import { useInvoiceCounts } from "../../../invoices/presentation/hooks/useInvoiceCounts";
import { useBillCounts } from "../../../bills/presentation/hooks/useBillCounts";
import { PendingCard } from "./PendingCard";
import { PaymentCard } from "./PaymentCard";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { Button } from "../../../../ui/components/Button";
import { FeatherDollarSign } from "@subframe/core";
import { FeatherAlertCircle } from "@subframe/core";
import { FeatherUserCheck } from "@subframe/core";

export const RecoSection: React.FC = () => {
  const navigate = useNavigate();

  // Fetch all reco data in parallel using single hook
  const {
    customerCredits,
    vendorCredits,
    bankPayments,
    queryCounts,
    loading: recoLoading,
  } = useRecoSummary();

  const { counts: invoiceCounts, loading: invoiceCountsLoading } =
    useInvoiceCounts();

  const { counts: billCounts, loading: billCountsLoading } = useBillCounts();

  // Check if all data is loading
  const isLoadingAll =
    recoLoading || invoiceCountsLoading || billCountsLoading;

  // Show centered loader while any data is loading
  if (isLoadingAll) {
    return (
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center bg-default-background px-8 py-8">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
          <span className="text-body font-body text-subtext-color">
            Loading reconciliation dashboard...
          </span>
        </div>
      </div>
    );
  }

  const formatAmount = (amount = 0, currencySymbol = "₹") =>
    `${currencySymbol}${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const chunkCurrencies = <
    T extends { currencyCode: string; currencySymbol?: string }
  >(
    currencies: T[] = []
  ) => {
    const rows: Array<Array<T | null>> = [];
    const chunkSize = 3;
    for (let i = 0; i < currencies.length; i += chunkSize) {
      const row: Array<T | null> = currencies.slice(i, i + chunkSize);
      while (row.length < chunkSize) {
        row.push(null);
      }
      rows.push(row);
    }
    if (rows.length === 0) {
      rows.push([null, null, null]);
    }
    return rows;
  };

  const renderCurrencySection = <
    T extends { currencyCode: string; currencySymbol?: string }
  >(params: {
    title: string;
    currencies?: T[];
    getCount: (curr: T) => number;
    getAmount: (curr: T) => number;
    onCardClick: (curr: T) => void;
  }) => {
    const { title, currencies = [], getCount, getAmount, onCardClick } = params;
    const rows = chunkCurrencies(currencies);

    return (
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <IconWithBackground
              variant="neutral"
              size="small"
              icon={<FeatherDollarSign />}
            />
            <span className="text-body-bold font-body-bold text-subtext-color">
              {title}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-3">
          {rows.map((row, rowIdx) => (
            <div
              className="flex w-full items-start gap-3"
              key={`${title}-row-${rowIdx}`}
            >
              {row.map((curr, colIdx) =>
                curr ? (
                  <div
                    key={`${title}-${curr.currencyCode}-${colIdx}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onCardClick(curr)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onCardClick(curr);
                      }
                    }}
                    className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-100 bg-neutral-50 px-4 py-3 cursor-pointer transition hover:border-neutral-200 hover:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {curr.currencyCode}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Count: {getCount(curr)}
                      </span>
                    </div>
                    <span className="text-heading-2 font-heading-2 text-default-font">
                      {formatAmount(getAmount(curr), curr.currencySymbol)}
                    </span>
                  </div>
                ) : (
                  <div
                    key={`${title}-empty-${colIdx}`}
                    className="flex grow shrink-0 basis-0 items-start rounded-md border border-solid border-neutral-100 bg-neutral-50 px-4 py-3 opacity-0 pointer-events-none"
                    aria-hidden="true"
                  />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 overflow-auto">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Reconciliation Dashboard
          </span>
          <span className="text-body font-body text-subtext-color">
            Overview of credits, pending invoices/bills, and bank payments
          </span>
        </div>
      </div>

      {/* Row 1: Credits Available */}
      <div className="flex w-full flex-col items-start gap-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Credits Available
        </span>
        <div className="flex w-full items-start gap-4 flex-wrap">
          {renderCurrencySection({
            title: "Customer Credits",
            currencies: customerCredits?.currencies,
            getCount: (curr) => curr.totalEntities || 0,
            getAmount: (curr) => curr.totalCredits || 0,
            onCardClick: (curr) =>
              navigate(`/reco/customer-credits?currency=${curr.currencyCode}`),
          })}
          {renderCurrencySection({
            title: "Vendor Credits",
            currencies: vendorCredits?.currencies,
            getCount: (curr) => curr.totalEntities || 0,
            getAmount: (curr) => curr.totalCredits || 0,
            onCardClick: (curr) =>
              navigate(`/reco/vendor-credits?currency=${curr.currencyCode}`),
          })}
        </div>
      </div>

      {/* Row 2: Outstanding Documents */}
      <div className="flex w-full flex-col items-start gap-4 mt-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Outstanding Documents
        </span>
        <div className="flex w-full items-start gap-4 flex-wrap">
          {renderCurrencySection({
            title: "Outstanding Invoices",
            currencies: customerCredits?.currencies,
            getCount: (curr) => curr.outstandingDocuments ?? 0,
            getAmount: (curr) => curr.totalOutstandingAmount ?? 0,
            onCardClick: (curr) =>
              navigate(
                `/reco/customer-outstanding-invoices?currency=${curr.currencyCode}`
              ),
          })}
          {renderCurrencySection({
            title: "Outstanding Bills",
            currencies: vendorCredits?.currencies,
            getCount: (curr) => curr.outstandingDocuments ?? 0,
            getAmount: (curr) => curr.totalOutstandingAmount ?? 0,
            onCardClick: (curr) =>
              navigate(
                `/reco/vendor-outstanding-bills?currency=${curr.currencyCode}`
              ),
          })}
        </div>
      </div>

      {/* Row 3: Pending Invoices & Bills */}
      <div className="flex w-full flex-col items-start gap-4 mt-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Pending Documents
        </span>
        <div className="flex w-full items-start gap-4">
          <PendingCard
            title="Pending Invoices"
            count={invoiceCounts?.pendingCount}
            onViewDetails={() => navigate("/dashboard/invoices")}
          />
          <PendingCard
            title="Pending Bills"
            count={billCounts?.pendingCount}
            onViewDetails={() => navigate("/dashboard/bills")}
          />
        </div>
      </div>

      {/* Row 4: Bank Payments */}
      <div className="flex w-full flex-col items-start gap-4 mt-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Bank Payments
        </span>
        <div className="flex w-full items-start gap-4">
          <PaymentCard
            title="Customer Payments Pending"
            count={bankPayments?.totalPendingCustomerPayments}
            amount={bankPayments?.totalPendingCustomerAmount}
            currencySymbol="₹"
            onViewDetails={() => navigate("/dashboard/cash-bank")}
            variant="success"
          />
          <PaymentCard
            title="Vendor Payments Pending"
            count={bankPayments?.totalPendingVendorPayments}
            amount={bankPayments?.totalPendingVendorAmount}
            currencySymbol="₹"
            onViewDetails={() => navigate("/dashboard/cash-bank")}
            variant="error"
          />
        </div>
      </div>

      {/* Row 5: Queries */}
      <div className="flex w-full flex-col items-start gap-4 mt-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Queries
        </span>
        <div className="flex w-full items-start gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
            <div className="flex items-center gap-2">
              <IconWithBackground
                variant="warning"
                icon={<FeatherAlertCircle />}
              />
              <span className="text-body-bold font-body-bold text-subtext-color">
                Assigned to me
              </span>
            </div>
            <div className="flex w-full items-end justify-between">
              <div className="flex flex-col items-start gap-1">
                <span className="text-body font-body text-subtext-color">
                  Count
                </span>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {queryCounts?.assignedToMe?.total || 0}
                </span>
              </div>
            </div>
            <Button size="small" onClick={() => navigate("/dashboard/queries?tab=assigned")}>
              View Details
            </Button>
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
            <div className="flex items-center gap-2">
              <IconWithBackground icon={<FeatherUserCheck />} />
              <span className="text-body-bold font-body-bold text-subtext-color">
                Raised by me
              </span>
            </div>
            <div className="flex w-full items-end justify-between">
              <div className="flex flex-col items-start gap-1">
                <span className="text-body font-body text-subtext-color">
                  Count
                </span>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {queryCounts?.raisedByMe?.total || 0}
                </span>
              </div>
            </div>
            <Button size="small" onClick={() => navigate("/dashboard/queries?tab=raised")}>
              View Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
